/**
 * Utility functions for generating content hashes
 */

/**
 * Generate a simple hash from a string
 * @param str String to hash
 * @returns Hash string
 */
export function generateSimpleHash(str: string): string {
  let hash = 0;
  if (str.length === 0) return hash.toString(16);

  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32bit integer
  }

  return Math.abs(hash).toString(16);
}

/**
 * Generate a content hash from a Blob or File
 * @param content Blob or File to hash
 * @returns Promise resolving to hash string
 */
export async function generateContentHash(content: Blob): Promise<string> {
  // Use the Web Crypto API if available
  if (typeof crypto !== "undefined" && crypto.subtle) {
    try {
      const buffer = await content.arrayBuffer();
      const hashBuffer = await crypto.subtle.digest("SHA-256", buffer);
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      const hashHex = hashArray
        .map((b) => b.toString(16).padStart(2, "0"))
        .join("");
      return hashHex;
    } catch (error) {
      console.error("Error generating content hash:", error);
      // Fall back to simple hash
      return generateSimpleHashFromBlob(content);
    }
  }

  // Fall back to simple hash if Web Crypto API is not available
  return generateSimpleHashFromBlob(content);
}

/**
 * Generate a simple hash from a Blob or File
 * @param content Blob or File to hash
 * @returns Promise resolving to hash string
 */
async function generateSimpleHashFromBlob(content: Blob): Promise<string> {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      resolve(generateSimpleHash(result));
    };
    reader.readAsText(content);
  });
}

/**
 * Generate a version string for a URL
 * @param url URL to version
 * @param version Version string
 * @returns Versioned URL
 */
export function addVersionToUrl(url: string, version: string): string {
  const urlObj = new URL(url, window.location.origin);
  urlObj.searchParams.set("v", version);
  return urlObj.toString();
}

/**
 * Extract version from a URL
 * @param url URL with version
 * @returns Version string or null if not found
 */
export function extractVersionFromUrl(url: string): string | null {
  try {
    const urlObj = new URL(url, window.location.origin);
    return urlObj.searchParams.get("v");
  } catch (error) {
    return null;
  }
}

/**
 * Generate a cache key from a URL
 * @param url URL to generate key for
 * @returns Cache key
 */
export function generateCacheKey(url: string): string {
  // Remove version parameter if present
  const urlObj = new URL(url, window.location.origin);
  urlObj.searchParams.delete("v");
  return urlObj.toString();
}
